import React, { useEffect, useRef, useState } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';
import dayjs, { dateDisplayFormat } from '~libs/dayjs';
import { parseSahApiErrors, readReasonCode } from '~support-at-home/lib/sahApiError';

import { Button, ErrorResponse, Modal, Spinner } from '~components';
import { faCircleCheck, faPenToSquare, faTriangleExclamation } from '~components/icon';
import { TableView } from '~components/table-view';

import { importItemColumns, importSkippedColumns } from '~support-at-home/forms/sah-budgets/importResults';

import { ImportBudgetsResponse, useImportBudgetsMutation } from '~support-at-home/actions/companyBudgets';

import ImportResultSection from './ImportResultSection';

interface ComponentProps {
  isOpen: boolean;
  onClose: () => void;
}

const MODAL_HEADER_ID = 'sah-budgets-import-modal-header';

// The import window is fixed by the server, not chosen here, and the confirm phase names it as
// PROSE rather than as calendar dates. Computing the dates here meant reimplementing the server's
// arithmetic (`buildImportWindow` in tp-assist-api's client_sah_budgets.mjs) against a different
// clock: a browser `dayjs()` and a server `dayjs()` disagree, so an AEST user before the UTC
// rollover was shown a window one day wider than the server would actually use. Nothing on this
// screen can now be precisely wrong. The result view still names real dates, because by then the
// server has returned the window it used.
const IMPORT_WINDOW_DESCRIPTION = 'The last 12 months and the next 6 months';

/**
 * ImportBudgetsModal (TAV-3168)
 *
 * Three exclusive phases in one modal, rather than closing on success and losing the outcome: a
 * confirmation naming the window that is about to be pulled, an explicit in-flight state (the
 * vendor page walk can run for a while), then a result view listing what was created, what was
 * updated, and every budget that was skipped with the reason it was skipped.
 *
 * Feedback lives entirely inside this modal, including failures - the budgets page deliberately has
 * no toast provider.
 */
const ImportBudgetsModal: React.FC<ComponentProps> = ({ isOpen, onClose }) => {
  const [result, setResult] = useState<ImportBudgetsResponse | null>(null);
  const [importBudgets, { isLoading, error, reset }] = useImportBudgetsMutation();

  // Reset to the confirm phase on the CLOSED -> OPEN transition only, so a stale result or error
  // from a previous import is not still on screen the next time the button is pressed.
  //
  // The ref guard is load-bearing, not defensive. RTK Query memoises `reset` on
  // [dispatch, fixedCacheKey, promise, requestId], so triggering the mutation changes its identity.
  // Without the guard, that identity change re-runs this effect WHILE the request is in flight and
  // calls reset(), tearing down the pending state: isLoading drops to false (the spinner flashes
  // and vanishes) and the error/result are discarded before they can ever render.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setResult(null);
      reset();
    }
    wasOpen.current = isOpen;
  }, [isOpen, reset]);

  const handleImport = async () => {
    try {
      const payload = await importBudgets().unwrap();
      setResult(payload);
    } catch {
      // Nothing further to do - the mutation's own error state is read below and rendered inside
      // the modal, above the description of what the user was about to do.
    }
  };

  // Both exits are the same door here: the parent's onClose already reloads the budgets list, and
  // an import that failed pre-flight has written nothing, so reloading costs one cheap query.
  const handleClose = () => onClose();

  // Errors render INSIDE the modal rather than via a toast that vanishes, and never as the raw
  // string. On the pre-flight failures the API names the exact cause (not connected, connection
  // inactive, no service provider ID, wrong PRODA environment) in `message`, and parseSahApiErrors
  // passes plain prose straight through. Plural, one message per line, because Services Australia
  // can return several distinct failures at once.
  const errorMessages = error ? parseSahApiErrors(getErrorMessage(error)) : [];

  const isFinished = Boolean(result);

  // A pre-flight refusal is the only failure the server can state definitively: it happens before
  // any vendor call and writes nothing, so "could not import" is exactly true. Everything else -
  // a dropped connection, a proxy cut on a long vendor page walk, an unhandled server throw -
  // arrives with no reason_code, and the client cannot know whether the run got as far as writing.
  // axiosWrapper sets no client-side timeout and the in-flight copy tells the user to sit through a
  // long run, so a mid-flight cut is a real outcome to word for, not a hypothetical.
  const isPreflightFailure = readReasonCode(error) !== null;

  // TableView requires an `id` on every row. Created and updated items carry one (the budget id);
  // skipped entries deliberately do not, because a skipped budget may not be identifiable at all,
  // so index them for rendering only.
  const skippedRows = (result?.skipped ?? []).map((skipped, index) => ({ ...skipped, id: index + 1 }));

  // Three exclusive phases - waiting, result, confirm - as early returns rather than a nested
  // ternary in the JSX (the repo lints `no-nested-ternary`).
  const renderBody = () => {
    if (isLoading) {
      // The server walks every page Services Australia holds for the window, so this can sit for a
      // while with nothing else on screen changing. Say so explicitly rather than leaving the
      // confirm text up looking inert.
      return (
        <div
          className="d-flex flex-column align-items-center justify-content-center py-5"
          data-cy="sah-budgets-import-modal-loading"
          role="status"
          aria-live="polite"
        >
          <Spinner loading />
          <p className="mt-3 mb-0 text-center">Importing budgets from Services Australia. This can take a moment.</p>
          <p className="mt-1 mb-0 text-center fst-italic">Do not close this window.</p>
        </div>
      );
    }

    if (result) {
      return (
        <div data-cy="sah-budgets-import-modal-result">
          {/*
            The caveat leads the tables rather than trailing them. It qualifies all three lists
            below, so reading it afterwards is reading it too late - the counts look like the whole
            picture until you know the run stopped early.
          */}
          {result.truncated && (
            <p className="mb-3 fst-italic" data-cy="sah-budgets-import-modal-truncated">
              Not every budget could be collected in one run. Run the import again to pick up the rest.
            </p>
          )}
          <p className="mb-3" data-cy="sah-budgets-import-modal-result-summary">
            {result.fetched} budgets were collected for{' '}
            {dayjs(result.window.effective_date_from).format(dateDisplayFormat)} to{' '}
            {dayjs(result.window.effective_date_to).format(dateDisplayFormat)}.
          </p>
          {/*
            All three sections always render, including at zero, because a count of zero is itself
            part of the outcome: "Created (0), Updated (12)" says something a hidden section does
            not. Precedent is the reconciliations process step, which renders its card
            unconditionally and lets it read (0).
          */}
          <ImportResultSection
            id="sah-budgets-import-created"
            title="Created"
            count={result.created_items.length}
            icon={faCircleCheck}
            iconClassName="text-success"
          >
            <TableView
              id="sah-budgets-import-created-table"
              columns={importItemColumns}
              data={result.created_items}
              readonly
              striped
              emptyMessage="No budgets were created"
            />
          </ImportResultSection>
          <ImportResultSection
            id="sah-budgets-import-updated"
            title="Updated"
            count={result.updated_items.length}
            icon={faPenToSquare}
            iconClassName="text-info"
          >
            <TableView
              id="sah-budgets-import-updated-table"
              columns={importItemColumns}
              data={result.updated_items}
              readonly
              striped
              emptyMessage="No budgets were updated"
            />
          </ImportResultSection>
          <ImportResultSection
            id="sah-budgets-import-skipped"
            title="Skipped"
            count={skippedRows.length}
            icon={faTriangleExclamation}
            iconClassName="text-warning"
          >
            {/*
              Inside the body rather than above the card: collapsed, the count is the whole message,
              and a caveat stranded above a closed section reads as if it qualifies the run itself.
            */}
            <p className="p-2 mb-0">
              These budgets were not imported. Nothing was created or changed in TurnPoint for them.
            </p>
            <TableView
              id="sah-budgets-import-skipped-table"
              columns={importSkippedColumns}
              data={skippedRows}
              readonly
              striped
              emptyMessage="No budgets were skipped"
            />
          </ImportResultSection>
        </div>
      );
    }

    return (
      <div data-cy="sah-budgets-import-modal-confirm">
        {/*
          A failure is an OUTCOME, so it leads. Underneath the description of what the user was
          about to do it reads as a footnote to a screen that otherwise looks unchanged. The
          description stays below it: the same action can be retried once the cause is cleared.
        */}
        {errorMessages.length > 0 && (
          <div className="mb-4" data-cy="sah-budgets-import-modal-error" role="alert">
            <h4 className="h6 fw-semibold text-danger mb-2">
              {isPreflightFailure ? 'TurnPoint could not import the budgets' : 'TurnPoint did not get a result back'}
            </h4>
            {errorMessages.map((message) => (
              <ErrorResponse key={message} message={message} className="justify-content-start m-0 mb-1" />
            ))}
            {/*
              Only on the unconfirmed branch. The outcome is unknown rather than negative, so the
              user is told to check before retrying - and that retrying is safe, because the import
              upserts: an already-imported budget is updated, never duplicated.
            */}
            {!isPreflightFailure && (
              <p className="mb-0 mt-2" data-cy="sah-budgets-import-modal-error-unconfirmed">
                Some budgets may already have been imported. Close this window and check the list before running the
                import again. Running it again is safe: a budget that is already in TurnPoint is updated, not
                duplicated.
              </p>
            )}
          </div>
        )}
        <p className="mb-2" data-cy="sah-budgets-import-modal-lede">
          This will import every Support at Home budget Services Australia holds for this provider that is effective in
          this window:
        </p>
        {/*
          The window is the one thing on this screen the user cannot change, so it is pulled out of
          the sentence and given its own line. Read at a glance, it is what tells them whether to go
          ahead.
        */}
        <p className="mb-3 fw-bold" data-cy="sah-budgets-import-modal-window">
          {IMPORT_WINDOW_DESCRIPTION}
        </p>
        <p className="mb-0" data-cy="sah-budgets-import-modal-matching-note">
          This will create or update budgets for <strong>ALL</strong> clients that match the Medicare identifier on
          TurnPoint.
        </p>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      // NOT a raw setter. Modal renders <ModalHeader toggle={() => setIsOpen(!isOpen)}>, so the
      // header's close icon bypasses the footer buttons entirely - and unlike the footer Cancel it
      // would not be disabled mid-import. Routing it through the same phase guard stops a user
      // dismissing an in-flight import, reopening from the still-enabled toolbar button and firing
      // the open transition that resets the live request.
      setIsOpen={(open) => {
        if (open || isLoading) return;
        handleClose();
      }}
      size="lg"
      header={<span id={MODAL_HEADER_ID}>Import budgets from Services Australia</span>}
      labelledBy={MODAL_HEADER_ID}
      includeFooter={true}
      data-cy="sah-budgets-import-modal"
      footer={
        isFinished ? (
          <Button size="sm" className="ps-4 pe-4" onClick={handleClose} dataCy="sah-budgets-import-modal-close-button">
            Close
          </Button>
        ) : (
          <div className="d-flex gap-2">
            <Button
              size="sm"
              className="ps-4 pe-4"
              color="success"
              loading={isLoading}
              // `loading` replaces the button's children with a spinner, losing its accessible name.
              aria-label="Import Budgets"
              onClick={handleImport}
              dataCy="sah-budgets-import-modal-confirm-button"
            >
              Import Budgets
            </Button>
            <Button
              size="sm"
              className="ps-4 pe-4"
              onClick={handleClose}
              disabled={isLoading}
              dataCy="sah-budgets-import-modal-cancel-button"
            >
              Cancel
            </Button>
          </div>
        )
      }
    >
      {renderBody()}
    </Modal>
  );
};

export default ImportBudgetsModal;
